/**
 * Abstract DAG Store interface
 * Implementations: IndexedDB (browser), SQLite (local)
 */
import type { DAGObject, ObjectType, ContentTypes, ProfileContent } from './types.js';
import { validateDAGObject, ValidationError } from '../validation/index.js';
import type { ValidationOptions } from '../validation/index.js';
import type { TombstoneContent } from '../content/tombstone.js';

export interface DAGStoreBackend {
  put(obj: DAGObject): Promise<void>;
  get(hash: string): Promise<DAGObject | null>;
  has(hash: string): Promise<boolean>;
  getAll(): Promise<DAGObject[]>;
  /** Get all hashes without loading full objects (optional optimization) */
  getAllHashes?(): Promise<string[]>;
  delete(hash: string): Promise<void>;
  clear(): Promise<void>;
}

export class DAGStore {
  private backend: DAGStoreBackend;
  /** Cache of tombstoned object hashes for efficient filtering */
  private tombstoneCache: Set<string> = new Set();
  /** Whether the tombstone cache has been initialized */
  private tombstoneCacheReady = false;

  constructor(backend: DAGStoreBackend) {
    this.backend = backend;
  }

  /**
   * Initialize the tombstone cache from stored objects
   * Call this after store is ready to enable tombstone filtering
   */
  async initTombstoneCache(): Promise<void> {
    const all = await this.backend.getAll();
    this.tombstoneCache.clear();
    for (const obj of all) {
      if (obj.type === 'tombstone') {
        const content = obj.content as TombstoneContent;
        this.tombstoneCache.add(content.targetHash);
      }
    }
    this.tombstoneCacheReady = true;
  }

  /**
   * Check if an object has been tombstoned (deleted)
   */
  isTombstoned(hash: string): boolean {
    return this.tombstoneCache.has(hash);
  }

  /**
   * Get all tombstone objects
   */
  async getTombstones(): Promise<DAGObject<TombstoneContent>[]> {
    const all = await this.backend.getAll();
    return all.filter(obj => obj.type === 'tombstone') as DAGObject<TombstoneContent>[];
  }

  /**
   * Store object, return hash
   */
  async put(obj: DAGObject): Promise<string> {
    await this.backend.put(obj);

    // Update tombstone cache if this is a tombstone
    if (obj.type === 'tombstone') {
      const content = obj.content as TombstoneContent;
      this.tombstoneCache.add(content.targetHash);
    }

    return obj.hash;
  }

  /**
   * Retrieve by hash
   */
  async get(hash: string): Promise<DAGObject | null> {
    return this.backend.get(hash);
  }

  /**
   * Check existence
   */
  async has(hash: string): Promise<boolean> {
    return this.backend.has(hash);
  }

  /**
   * Get all objects
   */
  async getAll(): Promise<DAGObject[]> {
    return this.backend.getAll();
  }

  /**
   * Get all object hashes (more efficient than getAll() for sync)
   */
  async getAllHashes(): Promise<string[]> {
    // Use optimized method if available
    if (this.backend.getAllHashes) {
      return this.backend.getAllHashes();
    }
    // Fall back to loading all objects
    const all = await this.backend.getAll();
    return all.map(obj => obj.hash);
  }

  /**
   * Get all objects of a specific type (excludes tombstoned objects)
   */
  async getByType<T extends ContentTypes>(type: ObjectType): Promise<DAGObject<T>[]> {
    const all = await this.backend.getAll();
    return all.filter(obj =>
      obj.type === type && !this.tombstoneCache.has(obj.hash)
    ) as DAGObject<T>[];
  }

  /**
   * Get objects by creator public key (excludes tombstoned objects)
   */
  async getByCreator(pubkey: string): Promise<DAGObject[]> {
    const all = await this.backend.getAll();
    return all.filter(obj =>
      obj.creator === pubkey && !this.tombstoneCache.has(obj.hash)
    );
  }

  /**
   * Get descendants (comments, votes on an object)
   */
  async getDescendants(hash: string): Promise<DAGObject[]> {
    const all = await this.backend.getAll();
    return all.filter(obj => {
      if (obj.type === 'comment' || obj.type === 'vote') {
        const content = obj.content as { target: string };
        return content.target === hash;
      }
      return obj.parents.includes(hash);
    });
  }

  /**
   * Get all articles sorted by timestamp (newest first, excludes tombstoned)
   */
  async getArticlesFeed(): Promise<DAGObject[]> {
    const articles = await this.getByType('article');
    // getByType already filters tombstoned objects
    return articles.sort((a, b) => b.timestamp - a.timestamp);
  }

  /**
   * Get vote counts for an object (excludes tombstoned votes)
   */
  async getVoteCounts(hash: string): Promise<{ up: number; down: number; total: number }> {
    // Return 0 counts if target is tombstoned
    if (this.tombstoneCache.has(hash)) {
      return { up: 0, down: 0, total: 0 };
    }

    const all = await this.backend.getAll();
    const votes = all.filter(obj => {
      if (obj.type !== 'vote') return false;
      // Exclude tombstoned votes
      if (this.tombstoneCache.has(obj.hash)) return false;
      const content = obj.content as { target: string; value: 1 | -1 };
      return content.target === hash;
    });

    let up = 0;
    let down = 0;
    for (const vote of votes) {
      const content = vote.content as { value: 1 | -1 };
      if (content.value === 1) up++;
      else down++;
    }

    return { up, down, total: up - down };
  }

  /**
   * Get the latest profile for a creator (by timestamp)
   */
  async getProfileByCreator(pubkey: string): Promise<DAGObject<ProfileContent> | null> {
    const profiles = await this.getByType<ProfileContent>('profile');
    const creatorProfiles = profiles.filter(p => p.creator === pubkey);
    if (creatorProfiles.length === 0) return null;
    // Return the most recent profile
    return creatorProfiles.sort((a, b) => b.timestamp - a.timestamp)[0];
  }

  /**
   * Get all profiles (for building a profile cache)
   */
  async getAllProfiles(): Promise<DAGObject<ProfileContent>[]> {
    return this.getByType<ProfileContent>('profile');
  }

  /**
   * Calculate karma for a user (sum of net votes on their non-deleted articles)
   */
  async getKarma(pubkey: string): Promise<number> {
    const all = await this.backend.getAll();
    const userArticles = all.filter(obj =>
      obj.type === 'article' &&
      obj.creator === pubkey &&
      !this.tombstoneCache.has(obj.hash)
    );

    let karma = 0;
    for (const article of userArticles) {
      const votes = await this.getVoteCounts(article.hash);
      karma += votes.total;
    }
    return karma;
  }

  /**
   * Get profile age in days (days since first profile was created)
   */
  async getProfileAge(pubkey: string): Promise<number> {
    const profiles = await this.getByType<ProfileContent>('profile');
    const userProfiles = profiles.filter(p => p.creator === pubkey);
    if (userProfiles.length === 0) return 0;

    // Find the oldest profile
    const oldest = userProfiles.sort((a, b) => a.timestamp - b.timestamp)[0];
    const daysSinceCreation = Math.floor(
      (Date.now() - oldest.timestamp) / (1000 * 60 * 60 * 24)
    );
    return daysSinceCreation;
  }

  /**
   * Get user statistics (excludes tombstoned content)
   */
  async getUserStats(pubkey: string): Promise<{
    karma: number;
    profileAge: number;
    articleCount: number;
    commentCount: number;
  }> {
    const all = await this.backend.getAll();
    const userObjects = all.filter(obj =>
      obj.creator === pubkey && !this.tombstoneCache.has(obj.hash)
    );

    const articleCount = userObjects.filter(obj => obj.type === 'article').length;
    const commentCount = userObjects.filter(obj => obj.type === 'comment').length;
    const karma = await this.getKarma(pubkey);
    const profileAge = await this.getProfileAge(pubkey);

    return { karma, profileAge, articleCount, commentCount };
  }

  /**
   * Clear all stored objects
   */
  async clear(): Promise<void> {
    await this.backend.clear();
  }
}

/**
 * In-memory store backend for testing
 */
export class MemoryStoreBackend implements DAGStoreBackend {
  private objects: Map<string, DAGObject> = new Map();

  async put(obj: DAGObject): Promise<void> {
    this.objects.set(obj.hash, obj);
  }

  async get(hash: string): Promise<DAGObject | null> {
    return this.objects.get(hash) ?? null;
  }

  async has(hash: string): Promise<boolean> {
    return this.objects.has(hash);
  }

  async getAll(): Promise<DAGObject[]> {
    return Array.from(this.objects.values());
  }

  async delete(hash: string): Promise<void> {
    this.objects.delete(hash);
  }

  async clear(): Promise<void> {
    this.objects.clear();
  }
}

/**
 * DAG Store with validation on put()
 *
 * Wraps a DAGStore backend and validates objects before storing.
 * Use this for production environments where you need to reject invalid objects.
 *
 * @example
 * const store = new ValidatingDAGStore(backend, { allowServerSigned: true });
 * await store.put(obj); // Throws ValidationError if invalid
 */
export class ValidatingDAGStore extends DAGStore {
  private validationOptions: ValidationOptions;

  constructor(backend: DAGStoreBackend, options: ValidationOptions = {}) {
    super(backend);
    this.validationOptions = options;
  }

  /**
   * Store object after validation
   * @throws {ValidationError} if object fails validation
   */
  async put(obj: DAGObject): Promise<string> {
    const result = validateDAGObject(obj, this.validationOptions);
    if (!result.valid) {
      throw new ValidationError(
        `Invalid DAG object: ${result.errors.join(', ')}`,
        result.errors
      );
    }
    return super.put(obj);
  }
}
